import type { EmployeeAdvance, EmployeeWithPrivate, Expense, PayrollEntry, PayrollRun } from '../types';
import {
  ROTULO_TIPO_CHAVE_PIX,
  arred,
  deslocarMes,
  rotuloMes,
  soDigitos,
} from '../../supabase/functions/_shared/payroll';
import { hojeLocal } from '../../supabase/functions/_shared/recebiveis';

// Modelo do "Relatório de pagamento da folha": tudo que o PDF e a planilha mostram sai daqui, já calculado e
// rotulado. Fica separado dos renderizadores para ser testado sem gerar arquivo.

// Situação do PAGAMENTO do líquido da pessoa (por PIX): já saiu do caixa, ainda vai sair no prazo ou está atrasado.
export type SituacaoPagamento = 'Pago' | 'A vencer' | 'Atrasado' | 'Prévia' | 'Sem pagamento' | 'Deve à empresa';

export interface LinhaRelatorio {
  nome: string;
  cargo: string;
  /** Só os 2 últimos dígitos + miolo mascarado: o relatório circula entre quem paga */
  cpf: string;
  tipoChavePix: string;
  chavePix: string;
  fixo: number;
  comissao: number;
  /** "Nível Blue · 2,3% de R$ 90.000,00" */
  comissaoDetalhe: string;
  comissaoBase: number;
  comissaoPercent: number;
  nivel: string;
  recebimentos: number;
  bonus: number;
  /** Vale/adiantamento tomado no mês */
  valeTomado: number;
  /** Parte do vale (e de saldo anterior) descontada NESTA folha */
  valeDescontado: number;
  /** Restante do vale que passa para a folha seguinte */
  saldoVale: number;
  jaPago: number;
  saldoAnterior: number;
  /** O que a pessoa ganhou no mês (fixo + comissão + bônus), antes dos descontos */
  apurado: number;
  /** Saldo final depois dos descontos (negativo = funcionário deve) */
  total: number;
  /** A conta por extenso quando há vale/desconto: "Apurado R$ 2.709,00 - descontado do vale R$ 709,00 (vale tomado R$ 1.500,00) - já recebido R$ 2.000,00 = R$ 0,00. Restante do vale: R$ 791,00 na folha de outubro de 2026." */
  explicacao: string;
  /** Líquido a pagar: apurado menos vale, já recebido e saldo anterior (nunca negativo) */
  aPagar: number;
  vencimento: string | null;
  pagoEm: string | null;
  situacao: SituacaoPagamento;
  custo: number;
  custoOrion: number;
  custoEvolua: number;
  observacao: string;
  avisos: string[];
}

export interface TotaisRelatorio {
  apurado: number;
  fixo: number;
  comissoes: number;
  bonus: number;
  valeTomado: number;
  valeDescontado: number;
  jaPago: number;
  aPagar: number;
  /** Restante de vales que passa para a próxima folha (negativo) */
  devendo: number;
  custo: number;
  custoOrion: number;
  custoEvolua: number;
  /** Líquido da folha já pago por PIX, ainda a vencer (no prazo) e atrasado (valor e quantidade).
   *  Líquido = pago + falta pagar; falta pagar = a vencer + atrasado. */
  pagos: { qtd: number; valor: number };
  pendentes: { qtd: number; valor: number };
  atrasados: { qtd: number; valor: number };
  /** Quanto do líquido ainda não saiu do caixa (a vencer + atrasado) */
  faltaPagar: { qtd: number; valor: number };
}

export interface RelatorioFolha {
  empresa: string;
  mes: string;
  rotuloMes: string;
  status: 'Rascunho' | 'Aprovada';
  dataPagamento: string;
  geradoEm: string;
  geradoPor: string;
  linhas: LinhaRelatorio[];
  totais: TotaisRelatorio;
  vales: Array<{ nome: string; data: string; valor: number; motivo: string }>;
  avisos: string[];
}

export function mascararCpf(cpf?: string | null): string {
  const d = soDigitos(cpf || '');
  if (d.length !== 11) return '';
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v: number) => `${String(v).replace('.', ',')}%`;

export interface DadosRelatorio {
  empresa: string;
  mes: string;
  run: PayrollRun;
  entradas: PayrollEntry[];
  funcionarios: EmployeeWithPrivate[];
  /** Contas a pagar geradas pela folha, por id */
  despesas: Map<number, Pick<Expense, 'id' | 'status' | 'due_date' | 'paid_at'>>;
  vales: EmployeeAdvance[];
  geradoPor?: string;
  hoje?: string;
  geradoEm?: Date;
}

/** Custo da pessoa no mês: o que ela ganhou (fixo + comissão + bônus). */
const custoDe = (e: PayrollEntry) => Number(e.fixed_amount) + Number(e.commission_amount) + Number(e.bonus_amount);

export function montarRelatorioFolha(d: DadosRelatorio): RelatorioFolha {
  const hoje = d.hoje ?? hojeLocal();
  const porId = new Map(d.funcionarios.map(f => [f.id, f]));
  const aprovada = d.run.status === 'approved';

  const linhas: LinhaRelatorio[] = d.entradas.map(e => {
    const f = e.employee_id ? porId.get(e.employee_id) : undefined;
    const total = arred(Number(e.total_due));
    const aPagar = Math.max(total, 0);
    const despesa = e.expense_id ? d.despesas.get(e.expense_id) : undefined;
    const vencimento = despesa?.due_date ?? d.run.pay_date;

    let situacao: SituacaoPagamento;
    if (total < 0) situacao = 'Deve à empresa';
    else if (total === 0) situacao = 'Sem pagamento';
    else if (!aprovada) situacao = 'Prévia';
    else if (despesa?.status === 'paid') situacao = 'Pago';
    else if (vencimento && vencimento < hoje) situacao = 'Atrasado';
    else situacao = 'A vencer';

    const custo = arred(custoDe(e));
    const valeDescontado = Number(e.advances_amount);
    const valeTomado = Number(e.details?.valeTomado ?? valeDescontado);
    const jaPago = Number(e.paid_amount);
    const saldoAnterior = Number(e.carry_in);
    // Restante do vale: o gravado na folha ou, em folhas antigas, o total negativo
    const saldoVale = arred(Math.max(Number(e.details?.saldoVale) || 0, -Math.min(total, 0)));
    const temDesconto = valeTomado > 0 || valeDescontado > 0 || jaPago > 0 || saldoAnterior < 0 || saldoVale > 0;
    const proximaFolha = rotuloMes(deslocarMes(d.mes, 1));
    const resultado = total < 0 ? `deve ${brl(Math.abs(total))}` : brl(total);
    const explicacao = temDesconto
      ? [
          `Apurado ${brl(custo)}`,
          valeDescontado > 0 || valeTomado > 0
            ? ` - descontado do vale ${brl(valeDescontado)}${valeTomado > 0 ? ` (vale tomado ${brl(valeTomado)}${saldoAnterior < 0 ? ` + saldo anterior ${brl(Math.abs(saldoAnterior))}` : ''})` : ''}`
            : '',
          jaPago > 0 ? ` - já recebido ${brl(jaPago)}` : '',
          ` = ${resultado}.`,
          saldoVale > 0 ? ` Restante do vale ${brl(saldoVale)}: desconta na folha de ${proximaFolha}.` : '',
        ].join('')
      : '';

    const orion = Math.min(Number(e.orion_amount) || 0, custo);
    const nivel = e.details?.nivel || '';
    const percent = Number(e.commission_percent) || 0;
    const base = Number(e.commission_base) || 0;
    const avisos: string[] = [];
    if (aPagar > 0 && !f?.pix_key) avisos.push('Sem chave PIX cadastrada');
    if (e.details?.semUsuario) avisos.push('Comissão zerada: funcionário sem login ligado');

    return {
      nome: e.employee_name,
      cargo: f?.position || '',
      cpf: mascararCpf(f?.cpf),
      tipoChavePix: f?.pix_key_type ? ROTULO_TIPO_CHAVE_PIX[f.pix_key_type] : '',
      chavePix: f?.pix_key || '',
      fixo: Number(e.fixed_amount),
      comissao: Number(e.commission_amount),
      comissaoDetalhe: percent > 0 || Number(e.commission_amount) > 0
        ? `${nivel ? `Nível ${nivel} · ` : ''}${percent > 0 ? `${pct(percent)} de ${brl(base)}` : 'valor informado'}${e.base_manual ? ' (base manual)' : ''}`
        : '',
      comissaoBase: base,
      comissaoPercent: percent,
      nivel,
      recebimentos: Number(e.details?.recebimentos) || 0,
      bonus: Number(e.bonus_amount),
      valeTomado,
      valeDescontado,
      saldoVale,
      jaPago,
      saldoAnterior,
      apurado: custo,
      explicacao,
      total,
      aPagar,
      vencimento: aPagar > 0 ? vencimento : null,
      pagoEm: despesa?.status === 'paid' ? despesa.paid_at ?? null : null,
      situacao,
      custo,
      custoOrion: arred(orion),
      custoEvolua: arred(custo - orion),
      observacao: e.notes || '',
      avisos,
    };
  });

  const soma = (fn: (l: LinhaRelatorio) => number) => arred(linhas.reduce((s, l) => s + fn(l), 0));
  const grupo = (sit: SituacaoPagamento) => {
    const sel = linhas.filter(l => l.situacao === sit);
    return { qtd: sel.length, valor: arred(sel.reduce((s, l) => s + l.aPagar, 0)) };
  };
  const pagos = grupo('Pago');
  const atrasados = grupo('Atrasado');
  const pendentesBase = grupo('A vencer');
  const totais: TotaisRelatorio = {
    apurado: soma(l => l.apurado),
    fixo: soma(l => l.fixo),
    comissoes: soma(l => l.comissao),
    bonus: soma(l => l.bonus),
    valeTomado: soma(l => l.valeTomado),
    valeDescontado: soma(l => l.valeDescontado),
    jaPago: soma(l => l.jaPago),
    aPagar: soma(l => l.aPagar),
    devendo: -soma(l => l.saldoVale),
    custo: soma(l => l.custo),
    custoOrion: soma(l => l.custoOrion),
    custoEvolua: soma(l => l.custoEvolua),
    pagos,
    atrasados,
    // A vencer = ainda no prazo (os atrasados aparecem à parte); em rascunho, tudo é prévia
    pendentes: aprovada ? pendentesBase : grupo('Prévia'),
    faltaPagar: aprovada
      ? { qtd: pendentesBase.qtd + atrasados.qtd, valor: arred(pendentesBase.valor + atrasados.valor) }
      : grupo('Prévia'),
  };

  const avisos: string[] = [];
  if (!aprovada) avisos.push('Folha ainda em rascunho: valores sujeitos a conferência. Não use como ordem de pagamento.');
  const semPix = linhas.filter(l => l.avisos.includes('Sem chave PIX cadastrada')).map(l => l.nome);
  if (semPix.length > 0) avisos.push(`Sem chave PIX cadastrada: ${semPix.join(', ')}.`);
  const devendo = linhas.filter(l => l.saldoVale > 0);
  if (devendo.length > 0) {
    avisos.push(`Restante de vale a descontar na próxima folha: ${devendo.map(l => `${l.nome} (${brl(l.saldoVale)})`).join(', ')}.`);
  }

  const nomeDe = (id: string) => porId.get(id)?.name || d.entradas.find(e => e.employee_id === id)?.employee_name || 'Funcionário removido';
  return {
    empresa: d.empresa,
    mes: d.mes,
    rotuloMes: rotuloMes(d.mes),
    status: aprovada ? 'Aprovada' : 'Rascunho',
    dataPagamento: d.run.pay_date,
    geradoEm: (d.geradoEm ?? new Date()).toLocaleString('pt-BR'),
    geradoPor: d.geradoPor || '',
    linhas,
    totais,
    vales: d.vales
      .map(v => ({ nome: nomeDe(v.employee_id), data: v.advance_date, valor: Number(v.amount), motivo: v.reason || '' }))
      .sort((a, b) => a.data.localeCompare(b.data)),
    avisos,
  };
}

/** Resumo de uma folha para a tela de histórico (sem precisar montar o relatório inteiro). */
export function resumoDaFolha(
  run: Pick<PayrollRun, 'status' | 'pay_date'>,
  entradas: PayrollEntry[],
  despesas: Map<number, Pick<Expense, 'status'>>,
  hoje: string = hojeLocal(),
) {
  let aPagar = 0, pago = 0, pendente = 0, devendo = 0, custo = 0, custoOrion = 0;
  for (const e of entradas) {
    const total = Number(e.total_due);
    const c = custoDe(e);
    custo += c;
    custoOrion += Math.min(Number(e.orion_amount) || 0, c);
    devendo -= Math.max(Number(e.details?.saldoVale) || 0, -Math.min(total, 0));
    if (total <= 0) continue;
    aPagar += total;
    if (run.status !== 'approved') continue;
    if (e.expense_id && despesas.get(e.expense_id)?.status === 'paid') pago += total;
    else pendente += total;
  }
  const atrasada = run.status === 'approved' && pendente > 0 && run.pay_date < hoje;
  return {
    pessoas: entradas.length,
    aPagar: arred(aPagar),
    pago: arred(pago),
    pendente: arred(pendente),
    devendo: arred(devendo),
    custo: arred(custo),
    custoOrion: arred(custoOrion),
    custoEvolua: arred(custo - custoOrion),
    atrasada,
    quitada: run.status === 'approved' && pendente === 0,
  };
}

export const nomeArquivoRelatorio = (mes: string, ext: 'pdf' | 'xlsx') => `folha-de-pagamento-${mes}.${ext}`;
